import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { safeStorage } from "@/lib/safeStorage";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_KEYS = {
  VISIT_COUNT: "pwa_visit_count",
  DISMISSED_PERMANENTLY: "pwa_banner_dismissed",
  DISMISSED_UNTIL: "pwa_banner_dismissed_until",
};

export const PWAInstallBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches 
      || (window.navigator as any).standalone === true;
    
    if (isStandalone) {
      return; // Don't show if already PWA
    }

    // Check if permanently dismissed
    const permanentlyDismissed = safeStorage.getItem(STORAGE_KEYS.DISMISSED_PERMANENTLY);
    if (permanentlyDismissed === "true") {
      return;
    }

    // Check if temporarily dismissed
    const dismissedUntil = safeStorage.getItem(STORAGE_KEYS.DISMISSED_UNTIL);
    if (dismissedUntil && new Date(dismissedUntil) > new Date()) {
      return;
    }

    // Increment and check visit count
    const currentCount = parseInt(safeStorage.getItem(STORAGE_KEYS.VISIT_COUNT) || "0", 10);
    const newCount = currentCount + 1;
    safeStorage.setItem(STORAGE_KEYS.VISIT_COUNT, newCount.toString());

    // Only show after 2+ visits
    if (newCount < 2) {
      return;
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Listen for install prompt (Android/Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      triggerShowBanner();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // For iOS, show banner immediately since they don't get the event
    if (isIOSDevice) {
      triggerShowBanner();
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const triggerShowBanner = () => {
    // Add a slight delay for better UX
    setTimeout(() => {
      setShowBanner(true);
      setTimeout(() => setIsAnimating(true), 50);
    }, 2000);
  };

  const handleInstall = async () => {
    if (isIOS) {
      navigate("/install");
      return;
    }

    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        safeStorage.setItem(STORAGE_KEYS.DISMISSED_PERMANENTLY, "true");
      }
      
      setDeferredPrompt(null);
      hideBanner();
    }
  };

  const handleLater = () => {
    // Dismiss for 24 hours
    const tomorrow = new Date();
    tomorrow.setHours(tomorrow.getHours() + 24);
    safeStorage.setItem(STORAGE_KEYS.DISMISSED_UNTIL, tomorrow.toISOString());
    hideBanner();
  };

  const handleClose = () => {
    // Dismiss permanently
    safeStorage.setItem(STORAGE_KEYS.DISMISSED_PERMANENTLY, "true");
    hideBanner();
  };

  const hideBanner = () => {
    setIsAnimating(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[100] transition-transform duration-300 ease-out ${
        isAnimating ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-card border-t border-border shadow-lg p-4 pb-safe">
        <div className="max-w-lg mx-auto">
          <div className="flex items-start gap-3">
            {/* App Icon */}
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0 shadow-md">
              <Smartphone size={28} className="text-primary-foreground" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-foreground">
                    {t("pwa.installTitle", "Install Gemurhood")}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t("pwa.installDescription", "Quick access, offline support, and faster loading")}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-muted rounded-full transition-colors flex-shrink-0"
                  aria-label="Close"
                >
                  <X size={18} className="text-muted-foreground" />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3">
                <Button
                  onClick={handleInstall}
                  size="sm"
                  className="flex-1 gap-2"
                >
                  <Download size={16} />
                  {isIOS 
                    ? t("pwa.howToInstall", "How to Install")
                    : t("pwa.install", "Install")
                  }
                </Button>
                <Button
                  onClick={handleLater}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                >
                  {t("common.later", "Later")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
